import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, LogOut, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import Logo from "@/components/brand/Logo";
import LanguageSwitcher from "@/components/i18n/LanguageSwitcher";
import { useLocalePath } from "@/i18n/LanguageSync";
import { useAuth } from "@/lib/auth";

const INTEGRATION_GUIDE_URL = (process.env.REACT_APP_BACKEND_URL || "") + "/api/v1/integration-guide";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthed, role, profile, logout } = useAuth();
  const { t } = useTranslation();
  const lp = useLocalePath();

  const dashHref = role === "admin" ? "/admin/dashboard" : "/partners/dashboard";
  const dashLabel = role === "admin" ? "Admin" : "Dashboard";
  const onSignOut = () => {
    logout();
    navigate(lp("/"), { replace: true });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const NAV = [
    { to: lp("/safe-to-spend"), label: t("nav.safeToSpend"), testId: "nav-safe-to-spend" },
    { to: lp("/ember"), label: t("nav.ember"), testId: "nav-ember" },
    { to: lp("/platform"), label: t("nav.apis"), testId: "nav-apis" },
    { to: lp("/developers"), label: t("nav.docs"), testId: "nav-docs" },
    { to: lp("/blog"), label: t("nav.blog"), testId: "nav-blog" },
    { to: lp("/status"), label: t("nav.status"), testId: "nav-status" },
    { to: lp("/about"), label: t("nav.about"), testId: "nav-about" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
        <Logo />

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              end={item.to === lp("/")}
              className={({ isActive }) =>
                `text-[14px] font-medium transition-colors ${
                  isActive
                    ? "text-[#0A1128]"
                    : "text-slate-600 hover:text-[#0A1128]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <a
            href={INTEGRATION_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-integration-guide"
            className="inline-flex items-center gap-1 text-[14px] font-medium text-slate-600 hover:text-[#1A4232] transition-colors"
          >
            {t("nav.guide")}
            <ExternalLink className="h-3 w-3" strokeWidth={2.2} />
          </a>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/for-employers"
            data-testid="nav-for-employers"
            className="text-[13px] text-slate-500 hover:text-[#0A1128] hover:underline underline-offset-4 transition-colors"
          >
            For Employers
          </Link>
          <LanguageSwitcher variant="compact" />
          {isAuthed ? (
            <>
              <Link
                to={dashHref}
                data-testid="nav-dashboard"
                className="text-[14px] font-medium text-slate-600 hover:text-[#0A1128] transition-colors"
                title={profile?.email}
              >
                {dashLabel}
              </Link>
              <button
                onClick={onSignOut}
                data-testid="nav-sign-out"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[13px] font-medium px-3.5 py-1.5 hover:border-slate-400 transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" />
                {t("actions.signOut")}
              </button>
            </>
          ) : (
            <Link
              to="/portal/login"
              data-testid="nav-partner-portal"
              className="text-[14px] font-medium text-slate-600 hover:text-[#0A1128] transition-colors"
            >
              {t("nav.partnerPortal")}
            </Link>
          )}
          <Link
            to={lp("/platform")}
            data-testid="nav-cta-explore-apis"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0A1128] text-white text-[13px] font-medium px-4 py-2 hover:bg-[#1E293B] transition-colors"
          >
            {t("nav.exploreApis")}
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.2} />
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-1">
          <LanguageSwitcher variant="compact" />
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="p-2 rounded-lg hover:bg-slate-100 text-[#0A1128]"
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden border-t border-slate-200 bg-white"
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-${item.testId}`}
                className={({ isActive }) =>
                  `py-2.5 text-[15px] font-medium ${
                    isActive ? "text-[#0A1128]" : "text-slate-600"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/portal/login"
              data-testid="mobile-nav-partner-portal"
              className="py-2.5 text-[15px] font-medium text-slate-600"
            >
              {t("nav.partnerPortal")}
            </Link>
            <a
              href={INTEGRATION_GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-integration-guide"
              className="inline-flex items-center gap-1.5 py-2.5 text-[15px] font-medium text-[#1A4232]"
            >
              {t("nav.guide")}
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.2} />
            </a>

            <div className="my-3 border-t border-slate-200" />
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold pb-1">
              For Business
            </p>
            <Link
              to="/for-employers"
              data-testid="mobile-nav-for-employers"
              className="py-2 text-[15px] font-medium text-slate-600"
            >
              For Employers
            </Link>
            <Link
              to="/for-employers/inquiry"
              data-testid="mobile-nav-for-employers-inquiry"
              className="py-2 text-[15px] font-medium text-slate-600"
            >
              Request a quote
            </Link>

            {isAuthed && (
              <button
                onClick={onSignOut}
                data-testid="mobile-nav-sign-out"
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[14px] font-medium px-4 py-2.5"
              >
                <LogOut className="h-4 w-4" />
                {t("actions.signOut")}
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
